import { FC, useState, useEffect } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import PageTitle from 'components/PageTitle';
import { Column } from 'react-table';
import { useNavigate } from 'react-router-dom';
import { MDIIcon } from 'pages/icons/MDIIcons';
import { Table } from 'react-bootstrap';
import { Spinner } from 'react-bootstrap';
import { useApiClient } from 'helpers/api/apiCoreNewClient';
import DomainsService from 'redux/domains/DomainsService';

interface GeofenceOverrideRequest {
    btParcelId: string;
    parcelId: number;
    taskId: string;
    customerAgeVerified: boolean;
    pickup: boolean;
    objectCreated: string;
    deliveryOrder: number;
    stopOrder: number;
    latitude: number;
    longitude: number;
    parcelStatus: string;
    stopLabel: string;
    geofenceOverrideStatus: string;
    parcelDeliveryStatus: string | null;
    sawGeofenceScreen: boolean | null;
    overrodeGeofence: boolean | null;
    scannedLatitude: number | null;
    scannedLongitude: number | null;
    geofenceOverrideLatitude: number | null;
    geofenceOverrideLongitude: number | null;
    requiredGeofenceBypassRequestLatitude: number | null;
    requiredGeofenceBypassRequestLongitude: number | null;
    driverUserId: number;
}

const GeofenceOverrides: FC = () => {
    const navigate = useNavigate();
    const client = useApiClient();
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<GeofenceOverrideRequest[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await DomainsService.getPendingGeofenceOverrides(client);
                setData(response || []);
            } catch (error) {
                console.error('Error fetching geofence overrides:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [client]);

    const columns: Column[] = [
        {
            Header: 'Parcel ID',
            accessor: 'btParcelId',
        },
        {
            Header: 'Stop Order',
            accessor: 'stopLabel',
        },
        {
            Header: 'Created At',
            accessor: 'objectCreated',
            Cell: ({ value }) => new Date(value).toLocaleString(),
        },
        {
            Header: 'Status',
            accessor: 'geofenceOverrideStatus',
            Cell: ({ value }) => (
                <span className={`badge bg-${value === 'Requested' ? 'warning' : 'success'}`}>
                    {value}
                </span>
            ),
        },
        {
            Header: 'Actions',
            accessor: 'taskId',
            Cell: ({ value }) => (
                <MDIIcon
                    icon="eye-outline"
                    className="primary-color"
                    size={24}
                    onClick={() => navigate(`/customer-service/geofence-overrides/${value}`)}
                    title="View Details"
                />
            ),
        },
    ];

    return (
        <>
            <PageTitle
                breadCrumbItems={[
                    { label: 'Customer Services', path: '/customer-service' },
                    { label: 'Geofence Overrides', path: '/customer-service/geofence-overrides', active: true },
                ]}
                title={'Geofence Override Requests'}
            />

            <Card>
                <Card.Body>
                    <Row>
                        <Col>
                            {isLoading ? (
                                <div className="text-center p-3">
                                    <Spinner animation="border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </Spinner>
                                </div>
                            ) : (
                                <Table responsive hover className="table-centered">
                                    <thead>
                                        <tr>
                                            {columns.map((column: any) => (
                                                <th key={column.Header}>{column.Header}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.map((row: any, i: number) => (
                                            <tr key={i}>
                                                {columns.map((column: any) => (
                                                    <td key={column.accessor}>
                                                        {column.Cell
                                                            ? column.Cell({ value: row[column.accessor] })
                                                            : row[column.accessor]}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </>
    );
};

export default GeofenceOverrides; 